import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Input, InputGroup, InputGroupAddon, Label } from 'reactstrap';
import EyeHide from 'assets/img/eye-hide.svg';
import EyeHideBlue from 'assets/img/eye-hide-blue.svg';
import EyeShow from 'assets/img/eye-show.svg';
import EyeShowBlue from 'assets/img/eye-show-blue.svg';

import lang from '../../lib/language';

import ClickableImage from '../ClickableImage/ClickableImage';

import { IPasswordProps } from './PasswordControl.types';

import styles from './PasswordControl.module.scss';

function PasswordControl(
  {
    id,
    name,
    placeholder,
    value,
    label,
    ariaLabel,
    visibleHint = false,
    visibleAddon = false,
    onChange,
    onValueChange,
    validationFlags,
    onSubmit,
    isSubmitButtonClicked,
  }: IPasswordProps,
) {
  const [inputValue, setInputValue] = useState<string>(!value ? '' : value);
  const [toggledTextType, setToggledTextType] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isLabelFloated, setIsLabelFloated] = useState<boolean>(false);
  const [isPristine, setIsPristine] = useState<boolean>(true);

  const hasPasswordError = isSubmitButtonClicked && validationFlags
    ? !validationFlags.eightSymbols
   || !validationFlags.maxCharacters
   || !validationFlags.uppercaseLetter
   || !validationFlags.lowercaseLetter
   || !validationFlags.specialChar
   || !validationFlags.number
   || !validationFlags.onlyLatinCharacters
    : false;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const targetValue = event.target.value;
    setInputValue(targetValue);

    if (onChange) {
      onChange(event);
    }

    onValueChange && onValueChange(targetValue);
  };

  const handleFocus = useCallback((): void => {
    setIsFocused(true);
    setIsPristine(false);
    setIsLabelFloated(true);
  }, []);

  const handleBlur = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const target = event.target as HTMLInputElement;

    let targetValue = target.value;

    const noWhitespacesCheck = targetValue.trim() !== '';

    setIsFocused(false);
    setIsLabelFloated(noWhitespacesCheck);

    if (!noWhitespacesCheck) {
      targetValue = '';
      setInputValue('');

      target.value = targetValue;

      onChange && onChange(event);
    }
  }, [onChange]);

  const getHintTextStyle = useCallback((isValid: boolean): string => {
    if (!isPristine) {
      return isValid ? styles.validRule : styles.invalidRule;
    }

    return '';
  }, [isPristine]);

  useEffect(() => {
    if (value) {
      setInputValue(value);
      setIsLabelFloated(true);
    } else {
      setIsLabelFloated(false);
    }

    return () => {
      setInputValue('');
    };
  }, [inputValue, value, setInputValue]);

  const handleOnKeyPress = (event: { key: any }) => {
    const { key } = event;
    key === 'Enter' && onSubmit?.();
  };

  return (
    <>
      <InputGroup
        className={[
          styles.inputContainer,
          styles.inputContainerDark,
        ].join(' ')}
      >
        <Input
          id={id}
          aria-label={ariaLabel}
          type={!toggledTextType ? 'password' : 'text'}
          autoComplete="off"
          name={name}
          value={inputValue}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={handleChange}
          onKeyPress={handleOnKeyPress}
          className={[
            styles.inputCapital,
            styles.inputControlDark,
            isFocused ? styles.inputFocusedDark : undefined,
            (isFocused || isLabelFloated) ? styles.inputWithHiddenLabel : '',
            hasPasswordError ? styles.inputErrorBorder : '',
          ].join(' ')}
          placeholder={placeholder}
        />

        {
          label && (!isFocused && !isLabelFloated) && (
          <Label className={[styles.label, styles.capitalLabel].join(' ')}>
            {label}
          </Label>
          )
        }

        {!visibleAddon ? null
          : (
            <InputGroupAddon addonType="append" className={styles.inputGroupAppend}>
              <ClickableImage
                alt="eye"
                className={styles.eyeImage}
                src={
                  !toggledTextType
                    ? (EyeShowBlue) || EyeShow
                    : (EyeHideBlue) || EyeHide
                }
                onClick={() => setToggledTextType(!toggledTextType)}
              />
            </InputGroupAddon>
          )}
      </InputGroup>
      {!visibleHint ? null
        : (
          <ul className={styles.hintsWrapper}>
            <li className={validationFlags && getHintTextStyle(validationFlags.eightSymbols)}>
              {lang.commonOnboardingSignInValidation8Chart()}
            </li>
            <li className={validationFlags && getHintTextStyle(validationFlags.maxCharacters)}>
              {lang.commonOnboardingSignInValidation32Chart()}
            </li>
            <li className={validationFlags && getHintTextStyle(validationFlags.uppercaseLetter)}>
              {lang.commonOnboardingSignInValidationUpperCaseLetter()}
            </li>
            <li className={validationFlags && getHintTextStyle(validationFlags.lowercaseLetter)}>
              {lang.commonOnboardingSignInValidationLowerCaseLetter()}
            </li>
            <li className={validationFlags && getHintTextStyle(validationFlags.specialChar)}>
              {lang.commonOnboardingSignInValidationSpecialSymbol()}
            </li>
            <li className={validationFlags && getHintTextStyle(validationFlags.number)}>
              {lang.commonOnboardingSignInValidationNumber()}
            </li>
            <li className={validationFlags && getHintTextStyle(validationFlags.onlyLatinCharacters)}>
              {lang.commonOnboardingSignInValidationLatinCharacters()}
            </li>
          </ul>
        )}
    </>
  );
}

export default PasswordControl;
